import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { ToastService } from '../services/toast.service';

/**
 * The workspace app shell only runs inside a /w/<slug> workspace the signed-in
 * user belongs to.
 *
 *  - Bare domain (no /w/<slug>): there is no default workspace — route the user
 *    to the picker, which auto-enters their sole workspace or lists several.
 *    Operators go to the operator console.
 *  - Wrong workspace (member of none): bounce to the picker. The server also
 *    refuses `POST /auth/login` through a workspace you're not a member of;
 *    this is the SPA-side equivalent for an already-open session.
 */
export const workspaceAccessGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const tenant = inject(TenantService);
  const toast = inject(ToastService);
  const router = inject(Router);

  return auth.loadUser().pipe(
    map(() => {
      const slug = tenant.currentSlug;
      const u = auth.user();
      if (!u) return true; // not signed in — authGuard handles it

      if (!slug) {
        // Bare domain — no workspace context.
        if (auth.isOperator()) return router.parseUrl('/operator');
        tenant.goToPicker();
        return false;
      }

      if (auth.isOperator()) return true; // operators may enter any workspace
      if (u.workspace && u.workspace.role) return true; // a real member of this workspace

      toast.error('This account doesn’t have access to that workspace.');
      tenant.goToPicker();
      return false;
    }),
  );
};
